<template>
  <!-- A whole-kip amount typed with thousands separators (3,600,000). A number
       input cannot group digits, and an operator reading 3600000 against a
       3,600,000 total is exactly how a zero goes missing. -->
  <div class="suffixed kip-input" :class="{'has-max': max !== null}">
    <input ref="input" class="fld-input" type="text" inputmode="numeric" autocomplete="off"
           :id="id" :value="display" :disabled="disabled" :placeholder="placeholder"
           @beforeinput="onBeforeInput" @input="onInput">
    <button v-if="max !== null" type="button" class="max-btn"
            :disabled="disabled || !(max > 0)" @click="fillMax">{{ $t("codAdvance.max") }}</button>
    <span class="suffix">{{ $t("codAdvanceReport.kip_unit") }}</span>
  </div>
</template>

<script>
// 15 digits stays inside Number's exact-integer range and the decimal(15,2)
// money columns; anything longer is a stuck key, not an amount.
const MAX_DIGITS = 15;

// The digits alone — the ກີບ suffix is drawn beside the field, not typed in it.
const grouped = new Intl.NumberFormat('en-US', {maximumFractionDigits: 0});

export default {
  name: 'KipInput',
  props: {
    // v-model: whole kip, or null while the field is empty.
    value: {type: Number, default: null},
    // Shows the ສູງສຸດ button, which fills this amount. Null = no button.
    max: {type: Number, default: null},
    disabled: {type: Boolean, default: false},
    placeholder: {type: String, default: '0'},
    id: {type: String, default: undefined},
  },
  computed: {
    display() {
      return this.value === null || this.value === undefined ? '' : grouped.format(this.value);
    },
  },
  created() {
    // Whether the edit in flight started from a bare caret (no selection).
    // Plain instance state: it never renders.
    this.caretOnly = null;
  },
  methods: {
    // The only point where the selection BEFORE the edit is still readable:
    // by `input` a deleted selection and a single deleted character look the
    // same.
    onBeforeInput(event) {
      this.caretOnly = event.target.selectionStart === event.target.selectionEnd;
    },
    onInput(event) {
      const el = event.target;
      // Kip has no minor unit. A trailing fraction — ".00" on a figure pasted
      // from the API, a stray "." — is DROPPED, never kept as more digits:
      // stripping only the dot turned 1,000.00 into 100,000. Any other dot is
      // a grouping mark (1.000.000) and goes like a comma does.
      const fraction = el.value.match(/\.\d{0,2}$/);
      const raw = fraction ? el.value.slice(0, fraction.index) : el.value;
      // Where the caret sits, counted in DIGITS from the end: the separators in
      // front of it move as the number regroups, the digits behind it do not.
      const caret = el.selectionStart === null ? raw.length : Math.min(el.selectionStart, raw.length);
      let digitsAfterCaret = raw.slice(caret).replace(/\D/g, '').length;
      let digits = raw.replace(/\D/g, '');

      // A delete from a bare caret that took only a separator would be undone
      // by the regrouping below, leaving the caret stuck behind the comma for
      // good. Take the digit beyond it instead, as the key means to. Never for
      // a SELECTION: selecting just the comma and deleting it must not take a
      // digit nobody selected.
      const before = this.value === null || this.value === undefined ? '' : String(this.value);
      const caretOnly = this.caretOnly === true;
      this.caretOnly = null;
      if (caretOnly && digits !== '' && digits === before) {
        if (event.inputType === 'deleteContentBackward') {
          const at = digits.length - digitsAfterCaret - 1;
          if (at >= 0) digits = digits.slice(0, at) + digits.slice(at + 1);
        } else if (event.inputType === 'deleteContentForward' && digitsAfterCaret > 0) {
          const at = digits.length - digitsAfterCaret;
          digits = digits.slice(0, at) + digits.slice(at + 1);
          digitsAfterCaret -= 1;
        }
      }

      digits = digits.replace(/^0+(?=\d)/, '').slice(0, MAX_DIGITS);
      digitsAfterCaret = Math.min(digitsAfterCaret, digits.length);
      const amount = digits === '' ? null : Number(digits);
      const text = amount === null ? '' : grouped.format(amount);

      // Written back directly: a keystroke that changes nothing (a letter, a
      // comma, a dot) leaves `value` unchanged, so Vue would not re-render and
      // the stray character would stay on screen.
      el.value = text;
      let position = text.length;
      for (let seen = 0; position > 0 && seen < digitsAfterCaret; position -= 1) {
        if (/\d/.test(text[position - 1])) seen += 1;
      }
      if (document.activeElement === el) {
        el.setSelectionRange(position, position);
      }
      this.$emit('input', amount);
    },
    fillMax() {
      this.$emit('input', this.max);
      this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
    },
  },
};
</script>

<style lang="scss" scoped>
@import './cod-advance-ui';

.kip-input .fld-input {
  font-variant-numeric: tabular-nums;
}

.kip-input.has-max .fld-input {
  padding-right: 118px;
}

.max-btn {
  position: absolute;
  right: 44px;
  top: 50%;
  transform: translateY(-50%);
  border: 0;
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  background: #dbe3fd;
  color: $cod-blue;
  cursor: pointer;

  &:hover {
    background: #c9d4fb;
  }

  &:disabled {
    background: #f2f4f7;
    color: #b3b9c5;
    cursor: not-allowed;
  }
}
</style>
